import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@heroui/react';
import { PosQueryKeys } from '@/api/queries/pos';
import { useCreateSalesTransactionMutation } from '@/api/queries/sales';
import { apiErrorMessage } from '@/utils';
import { useSeller } from '../useSeller';
import { useShift } from '../shift/useShift';
import { buildSaleRequest } from '../checkout';
import { formatGel } from '../sellerMeta';
import {
    appendPadKey,
    buildPayments,
    computeChange,
    deletePadKey,
    EMPTY_PAD_ENTRY,
    filledPadEntry,
    MONEY_EPSILON,
    parseCash,
    toMoney,
    type PadEntry,
    type PaymentMethod,
} from './paymentMeta';

/** Which leg of a combined split the numpad currently drives. */
export type CombinedLeg = 'cash' | 'card';

export type UsePaymentFlowParams = {
    /** Sends focus back to search — called once when the modal transitions from open to closed, on every route (success, Cancel, Esc, backdrop click). See `HomeFocusProvider`. */
    focusHome: () => void;
};

export type PaymentFlowValue = {
    /* Modal */
    isModalOpen: boolean;
    openModal: () => void;
    closeModal: () => void;

    /** Amount due: the cart's discounted `total` (bos-api charges the same). */
    due: number;
    /**
     * `false` while a discount in the cart no longer fits (a catalog refresh
     * moved a price under it): the due is not a figure to show or charge.
     * Every amount shown from it goes too (the header and the card terminal
     * show it as unknown, there is no change, `Exact` fills nothing, the
     * combined split balances nothing and reports no remainder or
     * overpayment) and Confirm is refused until the seller cancels and fixes
     * the cart.
     */
    isDueKnown: boolean;
    /** The sum of list prices, shown struck through when a discount applies. */
    subtotal: number;
    /** Line discounts + the sale discount; `0` for a sale without one. */
    discountTotal: number;

    /* Method */
    method: PaymentMethod;
    selectMethod: (method: PaymentMethod) => void;

    /* Shared numpad (drives the cash tender, or the active combined leg) */
    onPadKey: (key: string) => void;
    onPadDelete: () => void;
    padDeleteDisabled: boolean;

    /* Cash tender */
    cashEntry: string;
    cashReceived: number;
    change: number;
    setQuickCash: (amount: number) => void;
    setExactCash: () => void;

    /* Combined split (two legs that auto-balance to the due) */
    activeLeg: CombinedLeg;
    selectLeg: (leg: CombinedLeg) => void;
    setCombinedLeg: (leg: CombinedLeg, value: number | null) => void;
    combinedCash: number;
    combinedCard: number;
    /** Due still uncovered by the split — `0` when balanced, negative when over. */
    combinedRemaining: number;
    combinedOver: boolean;

    /**
     * Why the typed amount can't confirm, as a `pages` key for the dialog to
     * show — `null` when there's nothing to say. `…tooManyDigits` as soon as a
     * typed key doesn't fit an amount (a scan, not money); `…shortTender` once
     * a confirm was refused for a cash tender below the due, until it
     * changes; `…cardStrayInput` once keys were typed on Card, which takes no
     * amount, until the method changes or `Backspace` clears them.
     * `…discountProblem`, ahead of all of these, while `isDueKnown` is false.
     */
    entryNoticeKey: string | null;

    /* Submit */
    canConfirm: boolean;
    isSubmitting: boolean;
    /** Posts the sale — ignored while one is already in flight, whichever input (`Enter`, Confirm, a scanner's `Enter`) calls it. */
    confirm: () => void;
};

/**
 * Keys typed on Card, which takes no amount — a scan, most likely.
 * `'typed'`: the next confirm is refused (it's the scanner's own `Enter`).
 * `'refused'`: it was, and the input is dropped; the notice stays to say why,
 * and the next confirm charges the card.
 */
type CardStrayInput = 'none' | 'typed' | 'refused';

type EntryNoticeState = {
    cash: PadEntry;
    leg: PadEntry;
    cardStray: CardStrayInput;
    shortTenderRefused: boolean;
};

/** The one reason the entry can't confirm — see `PaymentFlowValue.entryNoticeKey`. */
const resolveEntryNoticeKey = (
    method: PaymentMethod,
    { cash, leg, cardStray, shortTenderRefused }: EntryNoticeState,
): string | null => {
    switch (method) {
        case 'cash':
            if (cash.overflow) {
                return 'seller.payment.tooManyDigits';
            }
            return shortTenderRefused ? 'seller.payment.shortTender' : null;
        case 'card':
            return cardStray === 'none'
                ? null
                : 'seller.payment.cardStrayInput';
        case 'combined':
            return leg.overflow ? 'seller.payment.tooManyDigits' : null;
    }
};

/** Buffer string for a derived leg value — empty (reads 0) when it's zero. */
const numToEntry = (value: number): string => (value > 0 ? String(value) : '');

/**
 * Drives the POS payment modal. The payable is the cart's `total`, after every
 * discount, computed with bos-api's rule so the server's cross-check of the
 * payment sum agrees (money stays server-authoritative). Each method resolves to
 * a `payments` array the server cross-checks: cash/card post a single
 * full-amount payment.
 *
 * `combined` splits the due across a cash + card pair that **auto-balance**: the
 * seller edits one leg (the active one, driven by the shared numpad or its own
 * field) and the other is always the remainder (`due − active`). Selecting the
 * other field makes it the active leg; the next keypress starts a fresh entry.
 *
 * `confirm` posts the sale, then clears the cart and closes on success; a server
 * rejection keeps the modal open to retry, and refreshes the catalog first —
 * a refusal usually means the prices or stock the cart was built from are no
 * longer what the server holds, so the seller's next attempt should be made
 * against current figures rather than the same wrong ones.
 *
 * An empty cash tender is treated as confirmable — it means the exact amount
 * (bos-seller#58: `F9`, `Enter` is the common cash-sale keystroke, with no
 * amount typed). A typed entry that overflowed never is (`appendPadKey`: more
 * than six integer digits or two decimals is a barcode scanned into payment,
 * whose own `Enter` must not sell); amounts the app fills in (Exact, a leg's
 * share of the due) are never capped. On Card, which takes no amount, a typed
 * key refuses the next confirm the same way. `confirm` holds a synchronous
 * lock from the moment it's called until the sale settles (rulebook §13), so
 * `Enter`, `Enter` posts once. `focusHome` fires once on every route that
 * closes the modal (success, Cancel, Esc, backdrop click) — the same
 * wasOpen-ref-plus-effect shape `useVoidConfirm` uses.
 */
export const usePaymentFlow = ({
    focusHome,
}: UsePaymentFlowParams): PaymentFlowValue => {
    const { t } = useTranslation('pages');
    const {
        cart,
        saleDiscount,
        subtotal,
        discountTotal,
        total,
        discountProblem,
        clearCart,
    } = useSeller();
    const { isOpen } = useShift();
    const mutation = useCreateSalesTransactionMutation();
    const queryClient = useQueryClient();

    // A refused sale re-reads the catalog. The success path is refreshed by the
    // mutation itself, which owns the "stock just moved" fact.
    const refreshCatalog = React.useCallback(
        () =>
            queryClient.invalidateQueries({ queryKey: PosQueryKeys.catalog() }),
        [queryClient],
    );

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [method, setMethod] = React.useState<PaymentMethod>('cash');
    const [cash, setCash] = React.useState<PadEntry>(EMPTY_PAD_ENTRY);
    const [activeLeg, setActiveLeg] = React.useState<CombinedLeg>('cash');
    const [leg, setLeg] = React.useState<PadEntry>(EMPTY_PAD_ENTRY);
    // When true, the next numpad key restarts the active leg's entry (so tapping
    // a field then typing enters a fresh amount rather than appending).
    const [legFresh, setLegFresh] = React.useState(true);
    // A confirm was refused for a short cash tender; cleared by any edit.
    const [shortTenderRefused, setShortTenderRefused] = React.useState(false);
    const [cardStray, setCardStray] = React.useState<CardStrayInput>('none');
    // The §13 submit lock. A ref, not state: a second `Enter` one keystroke
    // later lands before a re-render could show the first as pending.
    const submitLockRef = React.useRef(false);

    const due = toMoney(total);
    // Re-read on every render, so a refresh that breaks a discount while the
    // dialog is open blocks it at once, not only when it next opens (AC-10).
    const isDueKnown = discountProblem === null;

    const cashEntry = cash.text;
    const cashReceived = parseCash(cashEntry);
    // No change figure for an amount nobody tendered, or against an unknown due.
    const change =
        cash.overflow || !isDueKnown ? 0 : computeChange(due, cashReceived);

    // Combined legs auto-balance: the active leg holds what's typed, the other
    // is always the remainder so the split covers the due exactly.
    const legValue = parseCash(leg.text);
    const otherValue = isDueKnown ? Math.max(0, toMoney(due - legValue)) : 0;
    const combinedCash = activeLeg === 'cash' ? legValue : otherValue;
    const combinedCard = activeLeg === 'card' ? legValue : otherValue;
    // Nothing is short or over against an unknown due: the notice says why.
    const combinedRemaining = isDueKnown
        ? toMoney(due - combinedCash - combinedCard)
        : 0;
    const combinedOver = combinedRemaining < -MONEY_EPSILON;

    const entryNoticeKey = isDueKnown
        ? resolveEntryNoticeKey(method, {
              cash,
              leg,
              cardStray,
              shortTenderRefused,
          })
        : 'seller.payment.discountProblem';

    const resetEntries = React.useCallback(() => {
        setCash(EMPTY_PAD_ENTRY);
        setShortTenderRefused(false);
        setCardStray('none');
        setActiveLeg('cash');
        setLeg(EMPTY_PAD_ENTRY);
        setLegFresh(true);
    }, []);

    // Every edit to the tender withdraws a short-tender notice: it was about
    // the amount that was refused.
    const editCash = React.useCallback(
        (update: (current: PadEntry) => PadEntry) => {
            setCash(update);
            setShortTenderRefused(false);
        },
        [],
    );

    const openModal = React.useCallback(() => {
        setMethod('cash');
        resetEntries();
        setIsModalOpen(true);
    }, [resetEntries]);

    const closeModal = React.useCallback(() => {
        setIsModalOpen(false);
        setMethod('cash');
        resetEntries();
    }, [resetEntries]);

    // Focus goes home on every route that closes the modal — success, Cancel,
    // Esc, or a backdrop click all end up here, since they all flip
    // `isModalOpen` to false.
    const wasModalOpen = React.useRef(isModalOpen);
    React.useEffect(() => {
        if (wasModalOpen.current && !isModalOpen) {
            focusHome();
        }
        wasModalOpen.current = isModalOpen;
    }, [isModalOpen, focusHome]);

    // Switching method starts that method's entry clean — a cash tender must not
    // leak into the combined split or vice versa.
    const selectMethod = React.useCallback(
        (next: PaymentMethod) => {
            setMethod(next);
            resetEntries();
        },
        [resetEntries],
    );

    const onPadKey = React.useCallback(
        (key: string) => {
            switch (method) {
                case 'cash':
                    editCash((current) => appendPadKey(current, key));
                    return;
                case 'card':
                    // Card takes no amount: a key here is stray input.
                    setCardStray('typed');
                    return;
                case 'combined':
                    setLeg((current) =>
                        appendPadKey(legFresh ? EMPTY_PAD_ENTRY : current, key),
                    );
                    setLegFresh(false);
            }
        },
        [method, legFresh, editCash],
    );

    const onPadDelete = React.useCallback(() => {
        switch (method) {
            case 'cash':
                editCash(deletePadKey);
                return;
            case 'card':
                setCardStray('none');
                return;
            case 'combined':
                setLeg(deletePadKey);
                setLegFresh(false);
        }
    }, [method, editCash]);

    const padDeleteDisabled =
        method === 'card'
            ? cardStray !== 'typed'
            : (method === 'combined' ? leg : cash).text === '';

    // App-filled amounts: never capped (`filledPadEntry`).
    const setQuickCash = React.useCallback(
        (amount: number) => editCash(() => filledPadEntry(String(amount))),
        [editCash],
    );
    // Exact copies the due, so with an unknown due it has nothing to copy.
    const setExactCash = React.useCallback(() => {
        if (isDueKnown) {
            editCash(() => filledPadEntry(due.toFixed(2)));
        }
    }, [isDueKnown, due, editCash]);

    // Make a leg the active (editable) one. Re-tapping the current leg just arms
    // a fresh entry; switching seeds the buffer from the leg's current value so
    // the displayed numbers don't jump until the seller types.
    const selectLeg = React.useCallback(
        (next: CombinedLeg) => {
            if (next === activeLeg) {
                setLegFresh(true);
                return;
            }
            setActiveLeg(next);
            setLeg(
                filledPadEntry(
                    numToEntry(next === 'cash' ? combinedCash : combinedCard),
                ),
            );
            setLegFresh(true);
        },
        [activeLeg, combinedCash, combinedCard],
    );

    // Direct field edit (keyboard) — the edited leg becomes active and its value
    // drives the other.
    const setCombinedLeg = React.useCallback(
        (edited: CombinedLeg, value: number | null) => {
            setActiveLeg(edited);
            setLeg(
                filledPadEntry(
                    value === null || Number.isNaN(value) ? '' : String(value),
                ),
            );
            setLegFresh(false);
        },
        [],
    );

    const canConfirm = React.useMemo(() => {
        if (!isDueKnown) {
            return false;
        }
        switch (method) {
            case 'cash':
                // An untouched entry defaults to the exact amount.
                return (
                    !cash.overflow &&
                    (cash.text === '' || cashReceived + MONEY_EPSILON >= due)
                );
            case 'card':
                return cardStray !== 'typed';
            case 'combined':
                return (
                    !leg.overflow &&
                    due > 0 &&
                    Math.abs(combinedRemaining) < MONEY_EPSILON
                );
        }
    }, [
        isDueKnown,
        method,
        cash,
        cashReceived,
        due,
        cardStray,
        leg,
        combinedRemaining,
    ]);

    const confirm = React.useCallback(() => {
        if (submitLockRef.current) {
            return;
        }
        submitLockRef.current = true;
        let posted = false;
        try {
            // Defense in depth: PAY is disabled while the shift is closed, but
            // no sale may commit without an open till.
            if (!isOpen) {
                toast.danger(t('seller.shift.needShiftToSell'));
                return;
            }
            if (!isDueKnown) {
                // The notice already says why; nothing about the tender changes.
                return;
            }
            if (!canConfirm) {
                // An overflowed entry and an unbalanced split already say why.
                // A short tender says so now. Stray input on Card is dropped
                // (its notice stays), so the next confirm charges the card.
                if (method === 'cash' && !cash.overflow) {
                    setShortTenderRefused(true);
                }
                if (method === 'card') {
                    setCardStray('refused');
                }
                return;
            }

            const payments = buildPayments(method, due, {
                cash: combinedCash,
                card: combinedCard,
            });
            const built = buildSaleRequest(cart, saleDiscount, payments);
            if (!built.ok) {
                toast.danger(t('seller.payError'));
                return;
            }

            mutation.mutate(built.request, {
                onSuccess: (result) => {
                    if (result.success) {
                        toast.success(t('seller.paySuccess'), {
                            description: formatGel(result.response.total),
                        });
                        clearCart();
                        closeModal();
                    } else {
                        // Keep the modal open so the seller can adjust / retry.
                        // SEL-19: the refusal's code picks the message; an
                        // unknown code falls through to the server's own words.
                        void refreshCatalog();
                        toast.danger(apiErrorMessage(result.error, t));
                    }
                },
                onError: () => {
                    void refreshCatalog();
                    toast.danger(t('seller.payError'));
                },
                // Answered either way: the next confirm is a retry.
                onSettled: () => {
                    submitLockRef.current = false;
                },
            });
            posted = true;
        } finally {
            // A confirm that never reached the server holds nothing.
            if (!posted) {
                submitLockRef.current = false;
            }
        }
    }, [
        isOpen,
        isDueKnown,
        canConfirm,
        cash.overflow,
        method,
        due,
        combinedCash,
        combinedCard,
        cart,
        saleDiscount,
        mutation,
        clearCart,
        closeModal,
        refreshCatalog,
        t,
    ]);

    return React.useMemo(
        () => ({
            isModalOpen,
            openModal,
            closeModal,
            due,
            isDueKnown,
            subtotal: toMoney(subtotal),
            discountTotal,
            method,
            selectMethod,
            onPadKey,
            onPadDelete,
            padDeleteDisabled,
            cashEntry,
            cashReceived,
            change,
            setQuickCash,
            setExactCash,
            activeLeg,
            selectLeg,
            setCombinedLeg,
            combinedCash,
            combinedCard,
            combinedRemaining,
            combinedOver,
            entryNoticeKey,
            canConfirm,
            isSubmitting: mutation.isPending,
            confirm,
        }),
        [
            isModalOpen,
            openModal,
            closeModal,
            due,
            isDueKnown,
            subtotal,
            discountTotal,
            method,
            selectMethod,
            onPadKey,
            onPadDelete,
            padDeleteDisabled,
            cashEntry,
            cashReceived,
            change,
            setQuickCash,
            setExactCash,
            activeLeg,
            selectLeg,
            setCombinedLeg,
            combinedCash,
            combinedCard,
            combinedRemaining,
            combinedOver,
            entryNoticeKey,
            canConfirm,
            mutation.isPending,
            confirm,
        ],
    );
};
